import {BalloonEventModel} from 'ts-core';
import React, {useMemo} from 'react';
import RemoveBookmarkIcon from '../../assets/remove-bookmark-icon.svg?react';
import BookmarkIcon from '../../assets/bookmark-icon.svg?react';
import {useTranslations} from '../../contexts/Translations';
import {useUser} from '../../contexts/User';
import {trackMixpanel} from '../../utils/mixpanel';

export interface BookmarkButtonProps {
    event: BalloonEventModel;
    toolbar?: boolean;
    trackingSource?: string;
}

export function BookmarkButton({
                                   event,
                                   toolbar = false,
                                   trackingSource,
                               }: BookmarkButtonProps) {
    const [loading, setLoading] = React.useState(false);
    const [optimisticState, setOptimisticState] = React.useState<boolean | null>(null);
    const {translated} = useTranslations();
    const {user, toggleBookmark, bookmarkedIds, loadBookmarks} = useUser();

    const bookmarkSet = useMemo(() => new Set(bookmarkedIds), [bookmarkedIds]);

    const isBookmarked = (eventId: string) => bookmarkSet.has(eventId);

    const isBookmarkedState = useMemo(
        () => {
            if (optimisticState !== null) {
                return optimisticState;
            }
            return isBookmarked(event.id);
        },
        [optimisticState, bookmarkSet, event.id]
    );

    const performToggle = () => {
        if (!toggleBookmark) return;
        const currentState = isBookmarked(event.id);
        setOptimisticState(!currentState);
        setLoading(true);

        const action = currentState ? 'Remove' : 'Add';
        trackMixpanel('Bookmark', action, {
            eventId: event.id,
            eventName: event.title,
            source: trackingSource,
            guest: !user,
        });

        const authedUser = !!user;
        toggleBookmark(event)
            .then(() => (authedUser ? loadBookmarks() : undefined))
            .then(() => {
                setOptimisticState(null);
                setLoading(false);
            })
            .catch(() => {
                setOptimisticState(null);
                setLoading(false);
            });
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        // Guest path: store locally, no login prompt. Merge happens on future login.
        performToggle();
    };

    const buttonClassName = toolbar
        ? 'group flex justify-center items-center w-[36px] h-[36px] text-balloon hover:text-white bg-transparent hover:bg-balloon rounded-full p-2 transition-colors duration-150'
        : 'text-[#CC128C]  transition-transform hover:scale-110';

    const iconClassName = toolbar ? 'w-7 h-7 [stroke:theme(colors.balloon)] group-hover:[stroke:white]' : 'w-6 h-6 drop-shadow-lg';
    const bookmarkIconStyle = toolbar ? undefined : {stroke: '#ffffff'};

    return (
        <button
            onClick={handleClick}
            disabled={loading}
            className={`${buttonClassName} disabled:opacity-60 disabled:cursor-not-allowed`}
            aria-label={translated(isBookmarkedState ? "remove_from_bookmarks" : "add_to_bookmarks")}
            aria-busy={loading}
            title={translated(isBookmarkedState ? "remove_from_bookmarks" : "add_to_bookmarks")}
        >
            {isBookmarkedState ? (
                <RemoveBookmarkIcon className={iconClassName}/>
            ) : (
                <BookmarkIcon className={iconClassName} style={bookmarkIconStyle}/>
            )}
        </button>
    );
}
